import { Link } from "react-router-dom";
import { BookHeart, ArrowLeft, AlertTriangle } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="mt-8" data-testid={`privacy-section-${n}`}>
    <h2 className="font-serif text-2xl text-[#2C2A29]">{n}. {title}</h2>
    <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-[#3d3a38]">{children}</div>
  </section>
);

const Privacy = () => (
  <div className="min-h-screen bg-[#FBF8F3]">
    <header className="border-b border-border">
      <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-serif text-lg" data-testid="privacy-logo"><BookHeart className="w-5 h-5 text-primary" /> Eterna</Link>
        <Link to="/" className="text-sm text-muted-foreground hover:text-[#2C2A29] inline-flex items-center gap-1.5" data-testid="privacy-back"><ArrowLeft className="w-4 h-4" /> Torna alla home</Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-10 fade-in">
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2C2A29]">Informativa sulla Privacy</h1>
      <p className="text-muted-foreground mt-3">Ultimo aggiornamento: giugno 2026</p>

      <div className="mt-6 rounded-2xl border border-[#E7C9A9] bg-[#FBF0E4] p-4 flex gap-3 text-sm text-[#7a5320]" data-testid="privacy-draft-notice">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p><strong>Bozza da validare.</strong> Questo testo è una bozza fornita a scopo di partenza e <strong>deve essere revisionato e approvato da un legale</strong> prima della pubblicazione ufficiale. Non costituisce consulenza legale.</p>
      </div>

      <Section n="1" title="Titolare del trattamento">
        <p>Il Titolare del trattamento dei dati personali è <strong>Eterna</strong>, nella persona del suo fondatore <strong>Massimiliano Costantini</strong>.</p>
        <p>Per qualsiasi richiesta relativa ai tuoi dati puoi scrivere a: <a className="text-primary hover:underline" href="mailto:info@eterna.page">info@eterna.page</a>.</p>
        <p className="text-muted-foreground text-sm">[Da completare a cura del legale: ragione sociale, partita IVA, indirizzo della sede, eventuale nomina di un Responsabile della Protezione dei Dati (DPO).]</p>
      </Section>

      <Section n="2" title="Quali dati raccogliamo">
        <p>In qualità di piattaforma per la conservazione e il rilascio programmato di ricordi digitali, trattiamo le seguenti categorie di dati:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Dati dell'account:</strong> nome, indirizzo email, data di nascita e password (conservata in forma cifrata).</li>
          <li><strong>Contenuti che carichi:</strong> ricordi in forma di testo, foto, video, registrazioni audio, documenti, testamento e capsule del tempo.</li>
          <li><strong>Dati di persone terze (Legami):</strong> nomi, email e numeri di telefono di eredi, destinatari e tutori che tu decidi di indicare.</li>
          <li><strong>Documenti sensibili:</strong> eventuali certificati di decesso caricati per attivare il rilascio dei contenuti.</li>
          <li><strong>Dati di pagamento:</strong> gestiti direttamente dal fornitore di pagamento (Stripe); noi non conserviamo i dati completi della carta.</li>
          <li><strong>Dati tecnici:</strong> log di accesso, indirizzo IP, tipo di dispositivo e dati necessari al funzionamento del servizio.</li>
        </ul>
      </Section>

      <Section n="3" title="Finalità e basi giuridiche">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Fornire il servizio (creazione, conservazione e rilascio dei ricordi) — base: esecuzione del contratto.</li>
          <li>Inviare comunicazioni di servizio ed email automatiche a te e ai destinatari da te indicati — base: esecuzione del contratto e legittimo interesse.</li>
          <li>Gestire pagamenti e obblighi fiscali/contabili — base: obbligo legale.</li>
          <li>Garantire sicurezza, prevenzione abusi e assistenza — base: legittimo interesse.</li>
        </ul>
        <p className="text-muted-foreground text-sm">[Da verificare col legale: individuazione della base giuridica per il trattamento dei dati di terzi da te inseriti e per l'eventuale rilascio post-mortem.]</p>
      </Section>

      <Section n="4" title="Rilascio dei contenuti dopo la scomparsa">
        <p>Eterna consente di programmare il rilascio dei ricordi a una data, a un evento o dopo la tua scomparsa. Il rilascio post-mortem avviene solo dopo la comunicazione da parte dei tutori e, ove previsto, la verifica del certificato di decesso. I destinatari riceveranno esclusivamente i contenuti che hai loro dedicato.</p>
      </Section>

      <Section n="5" title="Fornitori e destinatari dei dati">
        <p>Per erogare il servizio ci avvaliamo di fornitori esterni che agiscono come responsabili del trattamento:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Stripe</strong> — elaborazione dei pagamenti.</li>
          <li><strong>Aruba</strong> — invio delle email di notifica (SMTP) e servizi di posta.</li>
          <li><strong>Fornitore di hosting e archiviazione file</strong> — conservazione sicura dei contenuti.</li>
        </ul>
        <p className="text-muted-foreground text-sm">[Da completare col legale: elenco definitivo dei fornitori, sedi di conservazione, eventuali trasferimenti extra-UE e relative garanzie.]</p>
      </Section>

      <Section n="6" title="Conservazione dei dati">
        <p>I contenuti sono conservati per la durata del piano scelto (a tempo determinato o "a vita") o finché mantieni attivo l'abbonamento. In caso di cessazione dell'abbonamento, lo spazio torna al limite gratuito e i contenuti eccedenti potrebbero non essere più visibili. Alla chiusura dell'account i dati vengono cancellati nei tempi tecnici necessari, salvo obblighi di legge.</p>
      </Section>

      <Section n="7" title="I tuoi diritti">
        <p>In qualità di interessato hai diritto di accesso, rettifica, cancellazione, limitazione, opposizione e portabilità dei tuoi dati, oltre al diritto di proporre reclamo all'Autorità Garante. Per esercitarli scrivi a <a className="text-primary hover:underline" href="mailto:info@eterna.page">info@eterna.page</a>.</p>
      </Section>

      <Section n="8" title="Sicurezza">
        <p>Adottiamo misure tecniche e organizzative adeguate (cifratura delle password, accesso protetto ai contenuti tramite token, trasmissione cifrata) per proteggere i tuoi dati. Nessun sistema è però sicuro al 100%.</p>
      </Section>

      <Section n="9" title="Modifiche a questa informativa">
        <p>Potremo aggiornare questa informativa nel tempo. Le modifiche rilevanti saranno comunicate tramite il servizio o via email.</p>
      </Section>

      <p className="mt-10 text-sm text-muted-foreground">Vedi anche i nostri <Link to="/termini" className="text-primary hover:underline" data-testid="privacy-to-terms">Termini di Servizio</Link>.</p>
    </main>

    <footer className="border-t border-border">
      <div className="max-w-3xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="flex items-center gap-2"><BookHeart className="w-4 h-4 text-primary" /> Eterna</span>
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <Link to="/termini" className="hover:text-[#2C2A29] transition-colors" data-testid="privacy-footer-terms">Termini di Servizio</Link>
          <a href="mailto:info@eterna.page" className="hover:text-[#2C2A29] transition-colors">info@eterna.page</a>
          <span>© 2026 Eterna</span>
        </div>
      </div>
    </footer>
  </div>
);

export default Privacy;
